import type { EventRoute } from '@/shared/infrastructure/events/eventRouter';
import { audioStoryKeys } from './queryKeys';

export function registerAudioStoriesEvents(
  register: (eventType: string, route: EventRoute) => void,
): void {
  register('audio-stories.created', {
    getQueryKeys: (p) => [
      audioStoryKeys.list(p.familyId as string),
      [...audioStoryKeys.all, 'byStory', p.storyId as string],
    ],
  });

  register('audio-stories.deleted', {
    getQueryKeys: (p) => [audioStoryKeys.list(p.familyId as string)],
  });

  // Per-block events: invalidate detail + byStory (workspace needs byStory to show progress).
  // Throttled on client side via React Query's default deduplication (staleTime).
  register('audio-stories.block.audio-generating', {
    getQueryKeys: (p) => [
      audioStoryKeys.detail(p.audioStoryId as string),
      ...(p.storyId ? [[...audioStoryKeys.all, 'byStory', p.storyId as string]] : []),
    ],
  });

  // byStory intentionally OMITTED: the story hub patches this event into its
  // byStory cache directly (StoryWorkspace setQueryData), so invalidating would
  // force a redundant refetch POST per completed block. `detail` stays for the
  // standalone audio editor, which is not patch-driven.
  register('audio-stories.block.audio-completed', {
    getQueryKeys: (p) => [
      audioStoryKeys.detail(p.audioStoryId as string),
    ],
  });

  // A failed block must reach the workspace (chip → error, block → retry) AND the
  // executor (assembly decision) — the completed event alone never fires for it.
  register('audio-stories.block.audio-failed', {
    getQueryKeys: (p) => [
      audioStoryKeys.detail(p.audioStoryId as string),
      ...(p.storyId ? [[...audioStoryKeys.all, 'byStory', p.storyId as string]] : []),
    ],
  });

  register('audio-stories.block.recording-uploaded', {
    getQueryKeys: (p) => [
      audioStoryKeys.detail(p.audioStoryId as string),
      ...(p.storyId ? [[...audioStoryKeys.all, 'byStory', p.storyId as string]] : []),
    ],
  });

  register('audio-stories.assembly.started', {
    getQueryKeys: (p) => [
      audioStoryKeys.detail(p.audioStoryId as string),
      ...(p.storyId ? [[...audioStoryKeys.all, 'byStory', p.storyId as string]] : []),
    ],
  });

  register('audio-stories.assembly.done', {
    getQueryKeys: (p) => [
      audioStoryKeys.detail(p.audioStoryId as string),
      ...(p.storyId ? [[...audioStoryKeys.all, 'byStory', p.storyId as string]] : []),
    ],
  });

  register('audio-stories.voice-settings.updated', {
    getQueryKeys: (p) => [audioStoryKeys.detail(p.audioStoryId as string)],
  });
}
